import { env } from "@/app/_utils/env";
import { clearCsrfToken } from "@/app/_utils/csrf";

const UI_USER_KEY = "cuo_user_profile";

// Endpoints whose auth failures must not redirect: a rejected login or the sign-out call
// itself would otherwise loop back into /login.
// auth/me/ is how the app discovers it has no session, so a 401 there is an answer,
// not an expiry to redirect on.
const SKIP = [
  "auth/me/",
  "auth/workos/authorize/",
  "auth/workos/password/",
  "auth/workos/callback/",
  "auth/logout/",
  "client-errors/",
];

let booted = false;

function isApiUrl(url: string): boolean {
  const base = env.apiBaseUrl;
  if (!base) return false;
  const absolute = new URL(url, window.location.origin).toString();
  return absolute.startsWith(new URL(base, window.location.origin).toString());
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

function boot() {
  if (booted) return;
  booted = true;
  try {
    localStorage.removeItem(UI_USER_KEY);
    clearCsrfToken();
  } catch {
    // ignore storage access errors
  }
  if (!window.location.pathname.startsWith("/login")) {
    window.location.replace("/login?expired=1");
  }
}

// The session lives in an httpOnly cookie the SPA cannot inspect, so an expired session is
// only ever discovered from a response. There is no shared API client to hook, hence fetch.
export function installAuthGuard() {
  const original = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const res = await original(input, init);
    if (res.status !== 401 && res.status !== 403) return res;

    const url = requestUrl(input);
    if (!isApiUrl(url) || SKIP.some((path) => url.includes(path))) return res;

    if (res.status === 401) {
      boot();
      return res;
    }

    // A 403 is usually a real permission denial. Only an expired session or a stale cached
    // CSRF token should boot -- and 403-for-unauthenticated is what the API returns until
    // the SessionAuthentication401 change is deployed.
    try {
      const body = await res.clone().text();
      if (
        body.includes("CSRF Failed") ||
        body.includes("Authentication credentials were not provided")
      ) {
        boot();
      }
    } catch {
      // unreadable body: treat as a normal 403
    }
    return res;
  };
}
